import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { useAuth } from "@/context/AuthContext";
import { api, getApiError } from "@/lib/api";
import { ArrowRight, Gift } from "lucide-react";

export default function Register() {
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const refFromUrl = params.get("ref") || "";
  const [form, setForm] = useState({
    name: "", email: "", password: "", role_intent: "homeowner",
    referral_code: refFromUrl, heard_about: "",
  });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (refFromUrl) setForm((f) => ({ ...f, referral_code: refFromUrl }));
  }, [refFromUrl]);

  const handleGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/portal";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/auth/register", {
        name: form.name,
        email: form.email,
        password: form.password,
        role_intent: form.role_intent,
        referral_code: form.referral_code || null,
        heard_about: form.heard_about || null,
      });
      setUser(data);
      toast.success("Welcome to the Guild");
      navigate("/portal");
    } catch (err) {
      toast.error(getApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const roles = [
    { v: "homeowner", l: "Homeowner" },
    { v: "seller",    l: "Seller" },
    { v: "buyer",     l: "Buyer" },
  ];

  return (
    <div className="min-h-screen bg-paper" data-testid="register-page">
      <Navbar />
      <main className="max-w-[1400px] mx-auto px-6 md:px-12 py-20 md:py-32 grid lg:grid-cols-12 gap-16">
        {/* Left — editorial column */}
        <div className="lg:col-span-5 hidden lg:block">
          <div className="overline mb-6">§ Admission</div>
          <h1 className="font-serif font-light text-5xl lg:text-6xl leading-[1.02] tracking-tight text-balance">
            Enter the<br />
            <span className="font-editorial italic text-stone-500">Guild.</span>
          </h1>
          <p className="mt-8 text-lg text-stone-600 font-light leading-relaxed max-w-md">
            Membership is by application only. Trustees review each account before granting access
            to the counterclaim process, the members marketplace, and the private ledger.
          </p>
          <div className="mt-12 hairline" />
          <ul className="mt-8 space-y-4 text-sm text-stone-600">
            {[
              "$1,500 yearly membership, auto-renewing",
              "1.5% service fee on settlements (not 8%)",
              "Six signed agreements, executed inside the portal",
              "Sheriff-sale cases triaged urgently by a trustee",
            ].map((line, i) => (
              <li key={i} className="flex gap-4 items-baseline">
                <span className="caption text-stone-400">{String(i + 1).padStart(2, "0")}</span>
                <span className="font-light">{line}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Right — form column */}
        <div className="lg:col-span-6 lg:col-start-7">
          <div className="border border-stone-200 bg-white p-8 md:p-12">
            <div className="overline mb-4">§ Apply</div>
            <h2 className="font-serif font-light text-3xl md:text-4xl leading-tight text-stone-900">
              Join the Guild
            </h2>
            <p className="text-sm text-stone-500 mt-2 font-light">
              Private members only. Trustees review every account.
            </p>

            {refFromUrl && (
              <div
                className="mt-8 flex items-start gap-3 border border-stone-900 bg-stone-50 p-5"
                data-testid="register-ref-banner"
              >
                <Gift className="h-5 w-5 text-stone-900 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-stone-800 leading-relaxed">
                  <span className="font-medium">$250 off applied.</span> You were referred by a
                  Guild member using code{" "}
                  <code className="font-mono bg-white border border-stone-200 px-2 py-0.5">{refFromUrl}</code>.
                </p>
              </div>
            )}

            <form onSubmit={onSubmit} className="mt-10 space-y-8">
              <div>
                <label htmlFor="name" className="label-luxury">Full name</label>
                <input
                  id="name"
                  required
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="input-luxury"
                  data-testid="register-name-input"
                />
              </div>
              <div>
                <label htmlFor="email" className="label-luxury">Email</label>
                <input
                  id="email"
                  type="email"
                  required
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="input-luxury"
                  data-testid="register-email-input"
                />
              </div>
              <div>
                <label htmlFor="password" className="label-luxury">Password · 6+ characters</label>
                <input
                  id="password"
                  type="password"
                  required
                  minLength={6}
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  className="input-luxury"
                  data-testid="register-password-input"
                />
              </div>

              <div>
                <div className="label-luxury">I am a…</div>
                <div className="grid grid-cols-3 border border-stone-300">
                  {roles.map((opt, i) => (
                    <button
                      type="button"
                      key={opt.v}
                      onClick={() => setForm({ ...form, role_intent: opt.v })}
                      className={`py-4 text-xs uppercase tracking-[0.18em] transition-colors ${
                        i > 0 ? "border-l border-stone-300" : ""
                      } ${
                        form.role_intent === opt.v
                          ? "bg-stone-900 text-white"
                          : "bg-white text-stone-700 hover:bg-stone-50"
                      }`}
                      data-testid={`register-role-${opt.v}`}
                    >
                      {opt.l}
                    </button>
                  ))}
                </div>
              </div>

              {!refFromUrl && (
                <div data-testid="register-ref-field">
                  <label htmlFor="ref" className="label-luxury flex items-center justify-between">
                    <span>Referral code · optional</span>
                    <span className="text-stone-900">Friend earns $250</span>
                  </label>
                  <input
                    id="ref"
                    placeholder="e.g. AGG123ABC"
                    value={form.referral_code}
                    onChange={(e) => setForm({ ...form, referral_code: e.target.value.toUpperCase() })}
                    className="input-luxury font-mono"
                    data-testid="register-ref-input"
                  />
                </div>
              )}

              <div data-testid="register-heard-field">
                <label htmlFor="heard" className="label-luxury">
                  How did you hear about us? · optional
                </label>
                <select
                  id="heard"
                  value={form.heard_about}
                  onChange={(e) => setForm({ ...form, heard_about: e.target.value })}
                  className="input-luxury bg-transparent"
                  data-testid="register-heard-select"
                >
                  <option value="">Select…</option>
                  <option value="referral">Friend or member referral</option>
                  <option value="google">Google search</option>
                  <option value="facebook">Facebook</option>
                  <option value="instagram">Instagram</option>
                  <option value="tiktok">TikTok</option>
                  <option value="youtube">YouTube</option>
                  <option value="news">News article / press</option>
                  <option value="court_lawyer">My lawyer or courthouse</option>
                  <option value="word_of_mouth">Word of mouth</option>
                  <option value="other">Other</option>
                </select>
              </div>

              <button
                type="submit"
                disabled={busy}
                className="btn-primary w-full justify-between disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="register-submit-btn"
              >
                {busy ? "Creating account…" : "Create my account"}
                <ArrowRight className="h-4 w-4" />
              </button>
            </form>

            <div className="rule my-10">
              <span className="caption">or</span>
            </div>

            <button
              type="button"
              onClick={handleGoogle}
              className="btn-outline w-full justify-center"
              data-testid="register-google-btn"
            >
              Continue with Google
            </button>

            <p className="text-sm text-center mt-10 text-stone-600">
              Already a member?{" "}
              <Link to="/login" className="text-stone-900 underline underline-offset-4 hover:text-stone-500">
                Sign in
              </Link>
            </p>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
